import { resource, weeklyCalendar } from "@planzone/dto";
import { Boundaries } from "@planzone/core";
import { Event, events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { reduceResource } from "../../common/resource";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  id: Boundaries["retrieveResource"]["request"],
): State {
  const index = state.findIndex((item) => item.id === id);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToPending(), id },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          resource: {
            ...resource,
            uuid: id,
            costProfile: null,
            weeklyCalendar: { ...weeklyCalendar, uuid: "" },
          },
          request: setRequestStateToPending(),
          id,
        },
      ];
}

function handleSuccess(
  state: State,
  resource: Boundaries["retrieveResource"]["success"],
): State {
  const index = state.findIndex((item) => item.id === resource.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToSuccess(), resource },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          id: resource.uuid,
          request: setRequestStateToSuccess(),
          resource,
        },
      ];
}

function handleFailure(
  state: State,
  { resourceId }: Boundaries["retrieveResource"]["failure"],
): State {
  const index = state.findIndex((item) => item.id === resourceId);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { ...state[index], request: setRequestStateToFailure() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          id: resourceId,
          request: setRequestStateToSuccess(),
          resource: {
            ...resource,
            uuid: resourceId,
            costProfile: null,
            weeklyCalendar: { ...weeklyCalendar, uuid: "" },
          },
        },
      ];
}

function handleAssignCostProfileToResource(
  state: State,
  {
    resource,
    costProfile,
  }: Boundaries["assignCostProfileToResource"]["success"],
): State {
  const index = state.findIndex((item) => item.id === resource.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          resource: { ...state[index].resource, costProfile },
        },
        ...state.slice(index + 1),
      ]
    : state;
}

function handleRemoveCostProfileFromResource(
  state: State,
  resource: Boundaries["removeCostProfileFromResource"]["success"],
): State {
  const index = state.findIndex((item) => item.id === resource.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          resource: { ...state[index].resource, costProfile: null },
        },
        ...state.slice(index + 1),
      ]
    : state;
}

function handleEditResourceWeeklyCalendar(
  state: State,
  {
    resource,
    calendar,
    nextCalendar,
  }: Boundaries["editResourceWeeklyCalendar"]["success"],
): State {
  const index = state.findIndex((item) => item.id === resource.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          ...state[index],
          resource: {
            ...state[index].resource,
            weeklyCalendar: { ...calendar, ...nextCalendar },
          },
        },
        ...state.slice(index + 1),
      ]
    : state;
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((item) => ({
    ...item,
    resource: { ...item.resource, ...reduceResource(item.resource, event) },
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveResourceRequested]: handleRequest,
      [events.retrieveResourceSucceeded]: handleSuccess,
      [events.retrieveResourceFailed]: handleFailure,
      [events.assignCostProfileToResourceSucceeded]:
        handleAssignCostProfileToResource,
      [events.removeCostProfileFromResourceSucceeded]:
        handleRemoveCostProfileFromResource,
      [events.editResourceWeeklyCalendarSucceeded]:
        handleEditResourceWeeklyCalendar,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
